import { useMemo } from 'react'

import { getPools } from '~/lib/getPools'
import {
	MOBILE_WIDTH_BREAKPOINT,
	TABLED_WIDTH_BREAKPOINT,
} from '~/lib/constants'
import { useClaimAll } from '~/lib/useClaimAll'
import { useMediaQuery } from '~/hooks/useMediaQuery'
import { useSetState } from '~/hooks/useSetState'
import { useClaimData } from '~/lib/useClaimData'

import { PageHeader } from '~/components/PageHeader'
import Tile from '~/components/Tile'
import { useRecentlyClaimed } from '~/lib/useRecentlyClaimed'
import { useRewards } from '~/lib/useRewards'
import { CROSSCHAIN_CONFIG } from '~/lib/config'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'
import { toBigNumber } from '~/lib/helpers'
import { SIS_DECIMALS } from '~/lib/SISDecimals'

import { DocumentationHint } from './DocumentationHint'
import { PoolWithReward } from './types'
import FarmDetails from './FarmDetails'
import FarmList from './FarmList'
import TotalRewardsBalance from './TotalRewardsBalance'
import { CROSSFARMING_DOCUMENTATION_HREF } from './utils'

import styles from './CrossChainFarmPage.pcss'

interface State {
	claimAllPending: boolean
	farm: PoolWithReward | null
	hintShow: boolean
}

const CLAIM_ALL_CHAIN_ID = CROSSCHAIN_CONFIG.claimAll.chainId

function CrossChainFarmPage() {
	const claimAll = useClaimAll()

	const isMobile = useMediaQuery(`(max-width: ${MOBILE_WIDTH_BREAKPOINT}px)`)
	const isTabled = useMediaQuery(`(max-width: ${TABLED_WIDTH_BREAKPOINT}px)`)

	const [state, setState] = useSetState<State>({
		hintShow: false,
		claimAllPending: false,
		farm: null,
	})

	const pools = getPools()
	const rewards = useRewards()

	const poolsWithRewards = useMemo((): PoolWithReward[] => {
		return pools.map((pool) => {
			const reward = rewards?.pools.find((poolReward) => {
				return (
					poolReward.chainIdA === pool.tokenA.chainId &&
					poolReward.chainIdB === pool.tokenB.chainFromId
				)
			})
			return { pool, reward }
		})
	}, [pools, rewards])

	const { claimData, claimDataPending } = useClaimData()

	const { on: enableRecentlyClaimed } = useRecentlyClaimed()

	const { claimAllPending, farm, hintShow } = state

	function onSubmit() {
		if (!claimData) {
			return
		}

		const { index, amount, account, proofs } = claimData

		setState({ claimAllPending: true })

		claimAll({ index, account, amount, proofs })
			.then((tx) => {
				tx.wait().then(() => {
					enableRecentlyClaimed()
					setState({ claimAllPending: false })
				})
			})
			.catch(() => {
				setState({ claimAllPending: false })
			})
	}

	function onFarmClick(data: PoolWithReward) {
		setState({ farm: data })
	}

	let buttonText
	let disabled
	if (claimAllPending) {
		disabled = true
		buttonText = 'Claiming....'
	} else if (claimDataPending) {
		disabled = true
		buttonText = 'Loading....'
	} else {
		const totalRewardsAmount = toBigNumber(
			SIS_DECIMALS,
			claimData?.amount ?? '0'
		)
		disabled = totalRewardsAmount.lte(0)
		buttonText = 'Claim all'
	}

	return (
		<div className={styles.container}>
			<Tile>
				<Tile.Header>
					<PageHeader
						title="Cross-chain Farming V1"
						onQuestionClick={() => {
							if (isTabled) {
								window.open(
									CROSSFARMING_DOCUMENTATION_HREF,
									'_blank'
								)

								return
							}

							setState({
								hintShow: !hintShow,
							})
						}}
					/>

					<TotalRewardsBalance
						totalRewards={rewards}
						pending={claimDataPending}
					>
						{isMobile && (
							<SwitchNetworkButton
								color="black"
								onClick={onSubmit}
								disabled={disabled}
								requiredChainId={CLAIM_ALL_CHAIN_ID}
							>
								{buttonText}
							</SwitchNetworkButton>
						)}
					</TotalRewardsBalance>
				</Tile.Header>

				<Tile.Item className={styles.content}>
					<FarmList
						data={poolsWithRewards}
						onFarmClick={onFarmClick}
					/>
				</Tile.Item>

				{!isMobile && (
					<Tile.Footer>
						<SwitchNetworkButton
							block
							color="black"
							disabled={disabled}
							onClick={onSubmit}
							requiredChainId={CLAIM_ALL_CHAIN_ID}
						>
							{buttonText}
						</SwitchNetworkButton>
					</Tile.Footer>
				)}
			</Tile>

			{/*{farm && (*/}
			{/*	<FarmDetails*/}
			{/*		className={styles.farmDetails}*/}
			{/*		data={farm}*/}
			{/*		onClose={() => {*/}
			{/*			setState({ farm: null })*/}
			{/*		}}*/}
			{/*	/>*/}
			{/*)}*/}

			{hintShow && !isTabled && (
				<DocumentationHint className={styles.hint} />
			)}
		</div>
	)
}

export default CrossChainFarmPage
